import React from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Trophy, Globe, FileText, Users } from "lucide-react";

const benefits = [
  {
    icon: <BadgeCheck className="text-white" size={28} />,
    title: "Attractive Returns",
    description: "Enjoy scalable upside with strong ROI potential as the company grows.",
  },
  {
    icon: <FileText className="text-white" size={28} />,
    title: "Transparency & Reports",
    description: "Access detailed investment agreements and quarterly progress updates.",
  },
  {
    icon: <Trophy className="text-white" size={28} />,
    title: "Recognition & Visibility",
    description: "Get featured on our platform, investor board, and select media campaigns.",
  },
  {
    icon: <Users className="text-white" size={28} />,
    title: "Early Venture Access",
    description: "Be the first to engage in publishing, author events, and new projects.",
  },
  {
    icon: <Globe className="text-white" size={28} />,
    title: "Social Impact",
    description: "Support a nationwide movement to spread literacy and education.",
  },
];

const InvestorBenefitsSection = () => {
  return (
    <section className="bg-gradient-to-b from-[#FFFAF8] to-white py-20 px-6 md:px-12">
      <div className="max-w-7xl mx-auto text-center">
        <motion.h2
          className="text-4xl md:text-4xl font-bold text-slate-900 mb-6"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
           Investor Benefits
        </motion.h2>
        <p className="text-lg text-gray-600 mb-16 max-w-2xl mx-auto">
          Here's what you'll gain by joining our mission to make India a nation of readers.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {/* First 3 cards */}
          {benefits.slice(0, 3).map((benefit, idx) => (
            <motion.div
              key={idx}
              className="group relative max-w-sm w-full px-6 py-8 rounded-3xl bg-white/70 border border-surface-200 backdrop-blur-glass shadow-glass hover:shadow-3d transition-all duration-300 hover:-translate-y-2 mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              viewport={{ once: true }}
            >
              {/* Hover overlay */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary to-primary-shade3 opacity-0 group-hover:opacity-100 transition-all duration-300 -z-10" />
              
              <div className="flex items-center justify-center w-14 h-14 mb-6 rounded-full mx-auto bg-gradient-to-br from-primary to-primary-shade3 shadow-inner-glow group-hover:bg-white/10 group-hover:from-white/20 group-hover:to-white/20 transition-all duration-300">
                {benefit.icon}
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3 text-center group-hover:text-white transition-colors duration-300">
                {benefit.title}
              </h3>
              <p className="text-sm text-gray-600 text-center leading-relaxed group-hover:text-white/90 transition-colors duration-300">
                {benefit.description}
              </p>
            </motion.div>
          ))}
          
          {/* Last 2 cards - centered */}
          <div className="lg:col-span-3 flex flex-col sm:flex-row justify-center items-center gap-10 sm:gap-8 lg:gap-10">
            {benefits.slice(3).map((benefit, idx) => (
              <motion.div
                key={idx + 3}
                className="group relative max-w-sm w-full px-6 py-8 rounded-3xl bg-white/70 border border-surface-200 backdrop-blur-glass shadow-glass hover:shadow-3d transition-all duration-300 hover:-translate-y-2"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: (idx + 3) * 0.1 }}
                viewport={{ once: true }}
              >
                {/* Hover overlay */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary to-primary-shade3 opacity-0 group-hover:opacity-100 transition-all duration-300 -z-10" />
                
                <div className="flex items-center justify-center w-14 h-14 mb-6 rounded-full mx-auto bg-gradient-to-br from-primary to-primary-shade3 shadow-inner-glow group-hover:bg-white/10 group-hover:from-white/20 group-hover:to-white/20 transition-all duration-300">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-3 text-center group-hover:text-white transition-colors duration-300">
                  {benefit.title}
                </h3>
                <p className="text-sm text-gray-600 text-center leading-relaxed group-hover:text-white/90 transition-colors duration-300">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InvestorBenefitsSection;